import React, { useEffect, useState } from "react";
import { View, StyleSheet, Platform, ViewStyle } from "react-native";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import BlankLayout from "../../../layouts/BlankLayout";
import AITutorChatContainer from "../../../containers/chat/AITutorChatContainer/AITutorChatContainer";
import LearningGoalsContainer, { Objective } from "../../../containers/companion/LearningGoalsContainer";
import AiTutorCompleteModal from "../../../containers/customCoursework/AiTutorCompleteModal";
import UpgradeModalContainer from "../../../containers/general/UpgradeModalContainer/UpgradeModalContainer";
import ExplorationMenu, {
  ExplorationMediaItem,
} from "../../../components/learning-activity/ExplorationMenu/ExplorationMenu";
import { colors, radii, spacing, typographyStyles, shadowStyles, webTransitions } from "../../../styles/design-system";

type ChatData = {
  id?: string;
  conversation?: Array<{
    id?: string;
    content?: string;
    sender?: string;
    created?: string;
    files?: Array<{ id: string; fileName: string }>;
    todos?: Array<{ id: string; name: string; date?: string | null }>;
    assignments?: Array<{ id: string; name?: string | null; due_date?: string | null }>;
  }>;
};

export interface AITutorChatLayoutContainerProps {
  resourceId: string;
  title: string;
  loading: boolean;
  backButtonText: string;
  onBackPress: () => void;
  onNextActivityPress?: () => void;
  onModalBackPress?: () => void;
  hasNextActivity: boolean;
  showUpgradeModal?: boolean;
  chatData: ChatData | undefined;
  chatLoading: boolean;
  objectives: Objective[];
  onObjectiveCompleted?: (objectiveIds: string[]) => void;
  onAllObjectivesCompleted?: () => void;
  onStreamingStateChange?: (isStreaming: boolean) => void;
  onStreamCompleted?: () => void;
  isScreenFocused?: boolean;
  explorationItems: ExplorationMediaItem[];
  onCloseUpgradeModal?: () => void;
  hideBackButton?: boolean;
  hideDisclaimer?: boolean;
}

const AITutorChatLayoutContainer = ({
  resourceId,
  title,
  loading,
  backButtonText,
  onBackPress,
  onNextActivityPress,
  onModalBackPress,
  hasNextActivity,
  showUpgradeModal = false,
  chatData,
  chatLoading,
  objectives,
  onObjectiveCompleted,
  onAllObjectivesCompleted,
  onStreamingStateChange,
  onStreamCompleted,
  isScreenFocused = true,
  explorationItems,
  onCloseUpgradeModal,
  hideBackButton = false,
  hideDisclaimer = false,
}: AITutorChatLayoutContainerProps) => {
  const [aiTutorCompleteModalVisible, setAiTutorCompleteModalVisible] = useState(false);
  const [isAiResponseStreaming, setIsAiResponseStreaming] = useState(false);
  const [shouldOpenCompletionModalAfterStreaming, setShouldOpenCompletionModalAfterStreaming] = useState(false);
  const [isBackHovered, setIsBackHovered] = useState(false);

  const handleStreamingStateChange = (isStreaming: boolean) => {
    setIsAiResponseStreaming(isStreaming);
    onStreamingStateChange?.(isStreaming);
  };

  const handleAiStreamCompleted = () => {
    if (!shouldOpenCompletionModalAfterStreaming) {
      return;
    }
    setAiTutorCompleteModalVisible(true);
    setShouldOpenCompletionModalAfterStreaming(false);
    onStreamCompleted?.();
  };

  useEffect(() => {
    if (!shouldOpenCompletionModalAfterStreaming || isAiResponseStreaming) {
      return;
    }
    setAiTutorCompleteModalVisible(true);
    setShouldOpenCompletionModalAfterStreaming(false);
  }, [isAiResponseStreaming, shouldOpenCompletionModalAfterStreaming]);

  const handleBackPress = () => {
    setAiTutorCompleteModalVisible(false);
    (onModalBackPress ?? onBackPress)();
  };

  const handleNextPress = () => {
    setAiTutorCompleteModalVisible(false);
    onNextActivityPress?.();
  };

  const handleClosePress = () => {
    setAiTutorCompleteModalVisible(false);
  };

  const handleAllObjectivesCompleted = () => {
    setShouldOpenCompletionModalAfterStreaming(true);
  };

  return (
    <BlankLayout>
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            {!hideBackButton && (
              <CustomButton
                onPress={onBackPress}
                size="s"
                text={backButtonText}
                styleType="secondary"
                onHover={(hovered) => setIsBackHovered(hovered)}
                style={[styles.backButton, isBackHovered && styles.backButtonHover]}
                textStyle={styles.backButtonText}
                accessibilityLabel="Exit button"
              />
            )}
            <CustomText
              text={title}
              useTranslationText={false}
              weight="bold"
              size="m"
              accessibilityLabel="Selected resource title"
              accessibilityRole="header"
              aria-level="1"
              role="heading"
              style={styles.headerTitle}
              numberOfLines={1}
              ellipsizeMode="tail"
            />
          </View>
        </View>

        <View style={styles.content}>
          <View style={styles.chatArea}>
            <CustomText
              useTranslationText={false}
              text="Conversation"
              role="heading"
              // @ts-expect-error aria-level is a web-only prop not in RN types
              aria-level={2}
              accessibilityRole="header"
              style={styles.srOnly}
            />
            {!loading ? <LearningGoalsContainer objectives={objectives} /> : null}
            <View style={styles.chatInner}>
              <AITutorChatContainer
                chatData={chatData}
                chatLoading={chatLoading}
                resourceId={resourceId}
                isScreenFocused={isScreenFocused}
                onObjectiveCompleted={onObjectiveCompleted}
                onAllObjectivesCompleted={handleAllObjectivesCompleted}
                onStreamingStateChange={handleStreamingStateChange}
                onStreamCompleted={handleAiStreamCompleted}
                hideDisclaimer={hideDisclaimer}
              />
            </View>
          </View>
          {explorationItems.length > 0 ? <ExplorationMenu items={explorationItems} /> : null}
        </View>

        <UpgradeModalContainer visible={showUpgradeModal} onClose={onCloseUpgradeModal ?? (() => {})} />
        <AiTutorCompleteModal
          visible={aiTutorCompleteModalVisible}
          onBackPress={handleBackPress}
          onClosePress={handleClosePress}
          onNextPress={handleNextPress}
          hasNextActivity={hasNextActivity}
          backButtonText={backButtonText}
        />
      </View>
    </BlankLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottomColor: colors.grey[200],
    borderBottomWidth: 1,
  },
  headerLeft: {
    paddingHorizontal: spacing.lg,
    paddingVertical: 10,
    gap: spacing.sm,
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
  },
  backButton: {
    minHeight: 36,
    padding: 0,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: radii.sm,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.1)",
    ...shadowStyles.sm,
    ...(Platform.OS === "web" ? webTransitions.fast : {}),
  },
  backButtonHover: {
    backgroundColor: colors.grey[100],
    ...shadowStyles.md,
    ...(Platform.OS === "web" ? { transform: [{ scale: 1.015 }] } : {}),
  },
  backButtonText: {
    ...typographyStyles.textSm,
    fontWeight: "500",
    color: colors.grey[700],
  },
  headerTitle: {
    color: colors.grey[900],
  },
  content: {
    flex: 1,
    position: "relative",
    backgroundColor: colors.grey[50],
  },
  chatArea: {
    flex: 1,
    position: "relative",
  },
  srOnly: {
    position: "absolute",
    width: 1,
    height: 1,
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    whiteSpace: "nowrap",
  } as ViewStyle,
  chatInner: {
    flex: 1,
    width: "100%",
    alignSelf: "stretch",
    paddingTop: 0,
    paddingBottom: 0,
  },
});

export default AITutorChatLayoutContainer;
